import { useCallback, useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';

/**
 * Draggable, persisted panel width (audit F5/F6). App.tsx had two near-identical ~30-line resize
 * blocks (sidebar + right drawer) differing only by range, storage key, and the sign of the drag
 * delta. This unifies them: `invert` flips the delta for a right-docked panel (dragging left widens
 * it). Listeners live on `window` for the drag's duration so the pointer can leave the thin handle
 * without dropping the drag; the width is clamped to [min, max] and mirrored to localStorage.
 */
export function usePanelWidth(opts: {
  storageKey: string;
  min: number;
  max: number;
  initial: number;
  invert?: boolean;
}): [number, (e: ReactMouseEvent) => void, () => void] {
  const { storageKey, min, max, initial, invert = false } = opts;

  const [width, setWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(storageKey));
    return saved >= min && saved <= max ? saved : initial;
  });

  useEffect(() => {
    localStorage.setItem(storageKey, String(width));
  }, [storageKey, width]);

  const startResize = useCallback(
    (e: ReactMouseEvent): void => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const onMove = (ev: MouseEvent): void => {
        const delta = ev.clientX - startX;
        setWidth(Math.min(max, Math.max(min, startW + (invert ? -delta : delta))));
      };
      const onUp = (): void => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [width, min, max, invert],
  );

  // Double-click a handle to reset to the default width.
  const reset = useCallback(() => setWidth(initial), [initial]);

  return [width, startResize, reset];
}
